import { STANDARD_HEADERS } from '@federa/const/http-header-names';
import { TIME_UNITS } from '@federa/const/time-units';

/** Default CORS preflight max-age (seconds): 1 day. */
export const DEFAULT_CORS_MAX_AGE = TIME_UNITS.DAY_S;

export const CORS_HEADER_NAMES = {
	ALLOW_ORIGIN: 'Access-Control-Allow-Origin',
	ALLOW_METHODS: 'Access-Control-Allow-Methods',
	ALLOW_HEADERS: 'Access-Control-Allow-Headers',
	ALLOW_CREDENTIALS: 'Access-Control-Allow-Credentials',
	MAX_AGE: 'Access-Control-Max-Age',
} as const;

export const CONTENT_TYPES = {
	JSON: 'application/json',
	FORM_URLENCODED: 'application/x-www-form-urlencoded',
	TEXT_PLAIN: 'text/plain',
	TEXT_HTML: 'text/html',
	XML: 'application/xml',
	OCTET_STREAM: 'application/octet-stream',
	MULTIPART_FORM_DATA: 'multipart/form-data',
	APPLICATION_PDF: 'application/pdf',
	IMAGE_PNG: 'image/png',
	RSS_XML: 'application/rss+xml',
	MARKDOWN: 'text/markdown',
	LD_JSON: 'application/ld+json',
	TSV: 'text/tab-separated-values; charset=UTF-8',
	/** Newline-delimited JSON -- one self-contained JSON value per line, which is what makes a body of it readable by a
	 *  consumer that never holds the whole object in memory. The declared type of a D1 table-export backup object. */
	NDJSON: 'application/x-ndjson',
} as const;

/**
 * The `Upgrade` header VALUE that names a WebSocket handshake -- one spelling for the producer that sets it and the
 * predicate that reads it (`isWebSocketRequest`, `const/http-request-init.ts`).
 *
 * IT IS THE CANONICAL CASING, NOT THE ACCEPTED ONE. RFC 9110 makes a header value's protocol token
 * case-insensitive, so the READER lowercases what arrived before comparing -- a proxy or client sending `WebSocket`
 * (Twilio's media edge among them) is a valid handshake and used to be refused with a silent 401 at every DO upgrade
 * gate in the fleet. Producers emit this exact string; readers must never compare against it verbatim.
 */
export const WEBSOCKET_UPGRADE_TOKEN = 'websocket';

/**
 * `Content-Encoding` tokens the OTLP ingest doors branch on, LOWERCASE because every reader lowercases what arrived
 * before comparing (the header value is case-insensitive, the same property `WEBSOCKET_UPGRADE_TOKEN` records).
 *
 * Homed here rather than at each door because the SAME three-way branch -- pass through, or pipe through a
 * `DecompressionStream`, or refuse -- is spelled at `flows/telemetry-relay-ingest.ts` and `flows/ai-telemetry-ingest.ts`,
 * and each door carried its own module-local identity-encoding const. `GZIP` and `DEFLATE` are additionally the argument
 * `DecompressionStream` takes, so the two doors cannot disagree about which encodings they claim to support and which
 * they can actually decompress.
 */
export const CONTENT_ENCODINGS = {
	IDENTITY: 'identity',
	GZIP: 'gzip',
	DEFLATE: 'deflate',
} as const;

export const CONNECTION_TYPES = {
	USER_DO: 'user-do',
	PROPERTY_DO: 'property-do',
} as const;

export const JSON_HEADERS = {
	[STANDARD_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
} as const;

export const CORS_ORIGIN_HEADER = {
	[CORS_HEADER_NAMES.ALLOW_ORIGIN]: '*',
} as const;

export const CORS_HEADERS = {
	[STANDARD_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
	[CORS_HEADER_NAMES.ALLOW_ORIGIN]: '*',
} as const;

/** CORS header set with a plain-text body (e.g. status-text 404 responses). */
export const TEXT_PLAIN_CORS_HEADERS = {
	...CORS_HEADERS,
	[STANDARD_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.TEXT_PLAIN,
} as const;
